import { Button, DataTable, ResultsCard, ResultsToolbar } from 'shared/components/ui';
import React, { useState, useEffect, useCallback } from 'react';
import { ManageSearch, Search, Clear, UnfoldMore } from '@mui/icons-material';

import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import { Alert } from '../../../shared/components';
import { trazabilidadAPI } from '../../../core/api/api';
import { useSortableTable } from '../../../shared/hooks/useSortableTable';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const PAGE_SIZE = 50;

const RECURSOS = [
  { value: '', label: 'Todos' },
  { value: '1', label: 'Email' },
  { value: '2', label: 'Adjunto' },
  { value: '3', label: 'WhatsApp' },
];

const getHoy = () => new Date().toISOString().split('T')[0];

const GrillaTrazabilidad = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [buscar, setBuscar] = useState('');
  const [resource, setResource] = useState('1');
  const [fechaDesde, setFechaDesde] = useState(getHoy());
  const [fechaHasta, setFechaHasta] = useState(getHoy());
  const [usuario, setUsuario] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { sortedData, requestSort, getSortIcon } = useSortableTable(logs);

  const cargar = useCallback(async (params, pagina = 1) => {
    setLoading(true);
    setError('');
    try {
      const queryParams = { page: pagina, pageSize: PAGE_SIZE };
      if (params.buscar) queryParams.buscar = params.buscar;
      if (params.resource) queryParams.resource = params.resource;
      if (params.fechaDesde) queryParams.fechaDesde = params.fechaDesde;
      if (params.fechaHasta) queryParams.fechaHasta = params.fechaHasta;
      if (params.usuario) queryParams.usuario = params.usuario;
      const res = await trazabilidadAPI.getLogs(queryParams);
      const responseData = res.data;
      setLogs(responseData.data || []);
      setTotalCount(responseData.totalCount || 0);
      setTotalPages(responseData.totalPages || 0);
      setPage(pagina);
    } catch {
      setError('Error al cargar la trazabilidad. Por favor intente más tarde.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar({ resource: '1', fechaDesde: getHoy(), fechaHasta: getHoy() }, 1);
  }, [cargar]);

  useEffect(() => {
    setUsuario('');
    trazabilidadAPI.getUsuarios(resource || null).then(res => setUsuarios(res.data || [])).catch(() => {});
  }, [resource]);

  const getFiltrosActuales = () => ({ buscar, resource, fechaDesde, fechaHasta, usuario });

  const handleBuscar = () => cargar(getFiltrosActuales(), 1);

  const handleLimpiar = () => {
    setBuscar('');
    setResource('1');
    setFechaDesde(getHoy());
    setFechaHasta(getHoy());
    setUsuario('');
    cargar({ resource: '1', fechaDesde: getHoy(), fechaHasta: getHoy() }, 1);
  };

  const handlePageChange = (newPage) => {
    cargar(getFiltrosActuales(), newPage);
  };

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <ManageSearch sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Trazabilidad
          </h2>
          <span className="page-subtitle">Registro de actividad del sistema</span>
        </div>

        <Alert message={error} onClose={() => setError('')} />

        <div className="filters-card">
          <div className="filters-header">
            <h3 className="filters-title">Filtros de Búsqueda</h3>
          </div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${buscar ? 'filter-active' : ''}`}>
                <label className="filter-label">Buscar</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Nombre, email o nro turno..."
                  value={buscar}
                  onChange={(e) => setBuscar(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleBuscar()}
                />
              </div>
              <div className={`filter-group ${fechaDesde ? 'filter-active' : ''}`}>
                <label className="filter-label">Desde</label>
                <input
                  type="date"
                  className="filter-input"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                />
              </div>
              <div className={`filter-group ${fechaHasta ? 'filter-active' : ''}`}>
                <label className="filter-label">Hasta</label>
                <input
                  type="date"
                  className="filter-input"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                />
              </div>
              <div className={`filter-group ${resource ? 'filter-active' : ''}`}>
                <label className="filter-label">Recurso</label>
                <select className="filter-select" value={resource} onChange={(e) => setResource(e.target.value)}>
                  {RECURSOS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div className={`filter-group ${usuario ? 'filter-active' : ''}`}>
                <label className="filter-label">Usuario</label>
                <select className="filter-select" value={usuario} onChange={(e) => setUsuario(e.target.value)}>
                  <option value="">Todos</option>
                  {usuarios.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div className="filter-actions">
                <Button onClick={handleBuscar} disabled={loading} className="btn-search">
                  <Search sx={{ fontSize: 18, mr: 0.5 }} />
                  {loading ? 'Buscando...' : 'Buscar'}
                </Button>
                <Button onClick={handleLimpiar} className="btn-clear">
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} />
                  Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left">
              <h3 className="results-title">Registros</h3>
            </div>
            <div className="results-info">
              <span className="results-count">{totalCount} registros encontrados</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('idTurno')} style={{ cursor: 'pointer' }}>
                    Nro Turno {getSortIcon('idTurno') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('nombre')} style={{ cursor: 'pointer' }}>
                    Nombre {getSortIcon('nombre') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('email')} style={{ cursor: 'pointer' }}>
                    Email/Telefono {getSortIcon('email') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('error')} style={{ cursor: 'pointer' }}>
                    Mensaje {getSortIcon('error') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('date')} style={{ cursor: 'pointer' }}>
                    Fecha {getSortIcon('date') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('usuario')} style={{ cursor: 'pointer' }}>
                    Usuario {getSortIcon('usuario') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('resource')} style={{ cursor: 'pointer' }}>
                    Recurso {getSortIcon('resource') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedData.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="empty-state">
                      {loading ? 'Cargando registros...' : 'No hay registros para mostrar'}
                    </td>
                  </tr>
                ) : (
                  sortedData.map((log) => (
                    <tr key={log.id}>
                      <td>{log.idTurno}</td>
                      <td>{log.nombre}</td>
                      <td>{log.email}</td>
                      <td>{log.error}</td>
                      <td>{log.date ? new Date(log.date).toLocaleString('es-AR') : ''}</td>
                      <td>{log.usuario}</td>
                      <td>{log.resource === 1 ? 'Email' : log.resource === 2 ? 'Adjunto' : log.resource === 3 ? 'WhatsApp' : log.resource}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </DataTable>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem' }}>
              <Button variant="outline-secondary" size="sm" onClick={() => handlePageChange(1)} disabled={page === 1}>«</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>‹</Button>
              <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
                Página {page} de {totalPages}
              </span>
              <Button variant="outline-secondary" size="sm" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>›</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => handlePageChange(totalPages)} disabled={page === totalPages}>»</Button>
            </div>
          )}
        </ResultsCard>
      </div>
      <Footer />
    </div>
  );
};

export default GrillaTrazabilidad;
